import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FairUseConfigFormValue, FairUseConfigPanelId } from "./fair-use-config-form-context"
import type { FairUseConfigFormState } from "./fair-use-utils"
import type { FairUseConfigUpdate } from "@/api/admin/fair-use"

import { AlertTriangle, RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"

import { FairUseConfigFormContext, useFairUseConfigForm } from "./fair-use-config-form-context"
import { applyFairUseSavedPanel } from "./fair-use-settings-utils"
import {
  fairUseConfigToFormState,
  fairUseErrorDescription,
  fairUseUnappliedConfigFields,
  isFairUseConfigConflict,
} from "./fair-use-utils"
import { useRegisterAdminDirty } from "../../admin-unsaved-changes-context"
import { useFairUseConfigQuery, useUpdateFairUseConfigMutation } from "../../hooks/queries/fair-use"

const NOTHING_DIRTY: Record<FairUseConfigPanelId, boolean> = {
  limits: false,
  plans: false,
  leadAllowance: false,
}

/**
 * The fair-use config document, edited by several tabs at once.
 *
 * Mounted ABOVE the Fair use area's tab switch, for two reasons. The tabs
 * unmount, so a panel holding its own edits would lose them on a tab switch —
 * an admin who types a price, checks a limit and comes back would find the box
 * empty. And the hash-OCC contract is per DOCUMENT: the `expectedHash` a Plans
 * save sends has to be the one the Settings tab is reading, so a single query
 * result serves every panel and every save sends `data.hash` as it stands.
 *
 * Each panel's Save still sends ONLY its own fields (`fairUseLimitsUpdate` /
 * `fairUsePlansUpdate` / `fairUseLeadAllowanceUpdate`), and reads back only
 * those — a commercial field inside a
 * delegated admin's payload answers 403 `platform_admin_required` and applies
 * nothing, and NEITHER payload ever carries `overrides`, which is written one
 * oid at a time from the people table.
 *
 * A save is CONFIRMED, never assumed: the PUT answers with the post-write
 * config and every field that was sent is read back out of it
 * ({@link fairUseUnappliedConfigFields}) before a success toast is allowed.
 * The route merges `updates` field by field, so a dropped field arrives inside
 * a 200 — and on a surface that decides what x-one invoices, a discarded price
 * shown as saved is the worst failure this feature has.
 */
export function FairUseConfigFormProvider({
  children,
  isPlatformAdmin,
}: {
  children: React.ReactNode
  /**
   * Passed down from the area, which resolves it against the customer
   * preview, rather than read from `/api/config` here — so a platform admin
   * previewing a customer admin sees the prices read-only, as that customer
   * would.
   */
  isPlatformAdmin: boolean
}) {
  const { t } = useTranslation()

  const { data, isLoading, refetch } = useFairUseConfigQuery()
  const updateMutation = useUpdateFairUseConfigMutation()

  const [form, setForm] = useState<FairUseConfigFormState | null>(null)
  // Per panel, so saving one does not quietly drop another's unsaved edits (and
  // so the area can tell the unsaved-changes guard that SOMETHING is unsaved).
  // While anything is dirty, background refetches must not wipe the form; only
  // a save or an explicit conflict reload lets server state back in.
  const [dirty, setDirty] = useState<Record<FairUseConfigPanelId, boolean>>(NOTHING_DIRTY)
  const [savingPanel, setSavingPanel] = useState<FairUseConfigPanelId | null>(null)
  const [hasConflict, setHasConflict] = useState(false)

  const isDirty = dirty.limits || dirty.plans || dirty.leadAllowance

  // The tab strip is not a route change, so the browser guard never fires on
  // it; this is what makes leaving the ADMIN page warn about unsaved prices.
  useRegisterAdminDirty("fair-use", isDirty)

  useEffect(() => {
    if (data && !isDirty) {
      setForm(fairUseConfigToFormState(data.config))
    }
  }, [data, isDirty])

  const updateForm = (panel: FairUseConfigPanelId, patch: Partial<FairUseConfigFormState>) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev))
    setDirty((prev) => ({ ...prev, [panel]: true }))
  }

  const reloadAfterConflict = () => {
    setHasConflict(false)
    setDirty(NOTHING_DIRTY)
    void refetch()
  }

  const save = (panel: FairUseConfigPanelId, updates: FairUseConfigUpdate | null) => {
    if (!updates || !data?.hash || savingPanel) return
    setSavingPanel(panel)
    updateMutation.mutate(
      { updates, expectedHash: data.hash },
      {
        onSuccess: (result) => {
          setSavingPanel(null)
          setDirty((prev) => ({ ...prev, [panel]: false }))
          setHasConflict(false)
          // Overlay the platform's answer onto the saved panel only. Tabs
          // unmount, so a full form reset would discard unsaved edits on the
          // other writer. Re-reading `/config` instead would ask whichever
          // worker happens to serve the GET, and only the one that served the
          // PUT has the new value until the gate's config TTL expires.
          if (result.config) {
            const savedConfig = result.config
            setForm((prev) =>
              prev
                ? applyFairUseSavedPanel(prev, savedConfig, panel)
                : fairUseConfigToFormState(savedConfig)
            )
          }
          const unapplied = fairUseUnappliedConfigFields(updates, result.config)
          if (unapplied.length > 0) {
            toast.error(
              t(
                "adminDashboard.fairUse.settings.toastSaveDiscarded",
                "The platform did not save every change"
              ),
              {
                // Naming the fields matters: "something went wrong" leaves the
                // admin retyping the same value into the same box.
                description: `${t(
                  "adminDashboard.fairUse.settings.saveDiscardedFields",
                  "These settings kept their previous values — reload and try again, and report it if it persists:"
                )} ${unapplied.join(", ")}`,
              }
            )
            return
          }
          toast.success(t("adminDashboard.fairUse.settings.toastSaved", "Fair use settings saved"))
        },
        onError: (error) => {
          setSavingPanel(null)
          if (isFairUseConfigConflict(error)) {
            setHasConflict(true)
            toast.error(
              t("adminDashboard.fairUse.settings.conflictTitle", "Settings changed elsewhere")
            )
          } else {
            // Surface the server's own reason — the generic string alone hides
            // which field failed.
            toast.error(
              t(
                "adminDashboard.fairUse.settings.toastSaveError",
                "Could not save the fair use settings"
              ),
              { description: fairUseErrorDescription(error, t) }
            )
          }
        },
      }
    )
  }

  const value: FairUseConfigFormValue = {
    form,
    data,
    isLoading,
    isPlatformAdmin,
    hasConflict,
    dirty,
    savingPanel,
    updateForm,
    save,
    reloadAfterConflict,
  }

  return (
    <FairUseConfigFormContext.Provider value={value}>{children}</FairUseConfigFormContext.Provider>
  )
}

/**
 * The conflict banner, rendered once by the area above the tab body: the
 * document is shared, so the warning belongs to every tab rather than to
 * whichever one happened to press Save.
 */
export function FairUseConfigConflictBanner() {
  const { t } = useTranslation()
  const { hasConflict, reloadAfterConflict } = useFairUseConfigForm()

  if (!hasConflict) return null

  return (
    <div className="border-destructive/50 bg-destructive/10 rounded-lg border p-4">
      <div className="text-destructive mb-1 flex items-center gap-2 font-medium">
        <AlertTriangle className="size-4" />
        {t("adminDashboard.fairUse.settings.conflictTitle", "Settings changed elsewhere")}
      </div>
      <p className="text-muted-foreground mb-3 text-sm">
        {t(
          "adminDashboard.fairUse.settings.conflictBody",
          "Someone else saved these settings after you opened them. Reload to get the latest values — your unsaved edits will be discarded."
        )}
      </p>
      <Button size="sm" variant="outline" onClick={reloadAfterConflict}>
        <RefreshCw className="mr-2 size-4" />
        {t("adminDashboard.fairUse.settings.conflictReload", "Reload settings")}
      </Button>
    </div>
  )
}

/** Shown by a tab while the shared document is still being read. */
export function FairUseConfigFormLoading() {
  return (
    <div className="flex items-center justify-center py-8">
      <RefreshCw className="text-muted-foreground size-6 animate-spin" />
    </div>
  )
}
